import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const vehicleTypes = [
  'Truck','Trailer','Container Truck','Flatbed Truck','Refrigerated Truck',
  'Tanker','Van','Pickup Truck','Heavy Truck','Light Truck','Other',
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 31 }, (_, i) => currentYear - i);

const Vehicles = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [vehicles,         setVehicles]         = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [filterType,       setFilterType]       = useState('all');
  const [showModal,        setShowModal]        = useState(false);
  const [editMode,         setEditMode]         = useState(false);
  const [submitLoading,    setSubmitLoading]    = useState(false);
  const [errors,           setErrors]           = useState({});

  const [formData, setFormData] = useState({
    vehicleid: null, companyid: companyId || null,
    registrationno: '', vehicletype: '', model: '', year: '', isactive: true,
  });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchVehicles();
  }, [companyId, yearId]);

  useEffect(() => { filterVehicles(); }, [searchTerm, filterType, vehicles]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Vehicles.php', { params: { companyid: companyId } });
      if (res.data.success) setVehicles(res.data.data || []);
      else alert('Failed to fetch vehicles: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching vehicles. Please check if backend is running.');
    } finally { setLoading(false); }
  };

  const filterVehicles = () => {
    let f = vehicles;
    if (filterType !== 'all') f = f.filter(v => v.VehicleType === filterType);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      f = f.filter(v =>
        v.RegistrationNo?.toLowerCase().includes(q) ||
        v.Model?.toLowerCase().includes(q) ||
        v.VehicleType?.toLowerCase().includes(q)
      );
    }
    setFilteredVehicles(f);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.registrationno.trim()) errs.registrationno = 'Registration number is required';
    else if (formData.registrationno.length < 3) errs.registrationno = 'Registration number must be at least 3 characters';
    if (formData.year && (isNaN(formData.year) || formData.year < 1900 || formData.year > currentYear + 1))
      errs.year = `Year must be between 1900 and ${currentYear + 1}`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const res = await apiClient[editMode ? 'put' : 'post']('/Vehicles.php', { ...formData, companyid: companyId, isactive: formData.isactive ? 1 : 0 });
      if (res.data.success) {
        alert(editMode ? 'Vehicle updated successfully!' : 'Vehicle created successfully!');
        closeModal(); fetchVehicles();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving vehicle. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (vehicle) => {
    setFormData({ vehicleid: vehicle.VehicleID, companyid: vehicle.CompanyID, registrationno: vehicle.RegistrationNo, vehicletype: vehicle.VehicleType || '', model: vehicle.Model || '', year: vehicle.Year || '', isactive: vehicle.IsActive === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      const res = await apiClient.delete('/Vehicles.php', { data: { vehicleid: id } });
      if (res.data.success) { alert('Vehicle deleted successfully!'); fetchVehicles(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting vehicle. Please try again.');
    }
  };

  const resetForm    = () => { setFormData({ vehicleid: null, companyid: companyId || null, registrationno: '', vehicletype: '', model: '', year: '', isactive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const uniqueTypes = [...new Set(vehicles.map(v => v.VehicleType).filter(Boolean))];

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] cursor-pointer ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  // ── no session ─────────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Truck size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Truck size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Vehicles Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage fleet vehicles and registration</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Vehicle
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by registration, model, or type..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        {uniqueTypes.length > 0 && (
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]">
            <option value="all">All Types</option>
            {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}

        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredVehicles.length} {filteredVehicles.length === 1 ? 'vehicle' : 'vehicles'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading vehicles...</div>
        ) : filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Truck size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No vehicles found</p>
            {(searchTerm || filterType !== 'all') && (
              <button onClick={() => { setSearchTerm(''); setFilterType('all'); }}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Registration No', 'Vehicle Type', 'Model', 'Year', 'Status', 'Added On', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.map(vehicle => (
                  <tr key={vehicle.VehicleID}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <Truck size={15} className="text-[#667eea] flex-shrink-0" />
                        <span className="font-mono font-bold text-[14px] text-gray-800">{vehicle.RegistrationNo}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      {vehicle.VehicleType ? (
                        <span className="inline-block px-2.5 py-1 rounded-full text-[12px] font-semibold bg-blue-100 text-blue-800">
                          {vehicle.VehicleType}
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-700">{vehicle.Model || '-'}</td>
                    <td className="px-4 py-3.5">
                      {vehicle.Year ? (
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar size={13} className="text-gray-400 flex-shrink-0" />
                          <span>{vehicle.Year}</span>
                        </div>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        vehicle.IsActive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {vehicle.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">{new Date(vehicle.CreatedAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(vehicle)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(vehicle.VehicleID)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[500px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Truck size={18} /> {editMode ? 'Edit Vehicle' : 'Add New Vehicle'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Section label */}
              <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide
                px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-5"
                style={{ borderLeftColor: NAVY }}>
                <Truck size={13} /> Vehicle Information
              </div>

              {/* Registration No */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Registration Number <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.registrationno}
                  onChange={e => setFormData({ ...formData, registrationno: e.target.value.toUpperCase() })}
                  maxLength="50" placeholder="e.g., ABC-1234, KSA-5678"
                  className={inputCls(errors.registrationno) + ' font-mono font-semibold text-[16px]'} autoFocus />
                {errors.registrationno && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.registrationno}</p>}
                <p className="text-[11px] text-gray-400 mt-1">Enter license plate or registration number</p>
              </div>

              {/* Type + Year */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Vehicle Type</label>
                  <select value={formData.vehicletype} onChange={e => setFormData({ ...formData, vehicletype: e.target.value })} className={selectCls(false)}>
                    <option value="">-- Select Type --</option>
                    {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Year</label>
                  <select value={formData.year} onChange={e => setFormData({ ...formData, year: e.target.value })} className={selectCls(errors.year)}>
                    <option value="">-- Select Year --</option>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  {errors.year && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.year}</p>}
                </div>
              </div>

              {/* Model */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Model</label>
                <input type="text" value={formData.model} onChange={e => setFormData({ ...formData, model: e.target.value })}
                  maxLength="100" placeholder="e.g., Mercedes-Benz Actros, Volvo FH16" className={inputCls(false)} />
                <p className="text-[11px] text-gray-400 mt-1">Make and model of the vehicle</p>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive}
                    onChange={e => setFormData({ ...formData, isactive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
                <p className="text-[11px] text-gray-400 mt-1 pl-1">Inactive vehicles won't appear in assignment lists</p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update Vehicle' : 'Add Vehicle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Vehicles;